import React from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import { ReactComponent as MalabarLogo } from "../src/assets/img/img_logo.png"; // Assuming this is your main logo
import UserIcon from "../src/assets/img/icons/user.png";
import MetalIcon from "../src/assets/img/icons/metal.png";
import ItemIcon from "../src/assets/img/icons/item.png";
import DesignIcon from "../src/assets/img/icons/design.png";
import FinishIcon from "../src/assets/img/icons/finish.png";
import AnalyticsIcon from "../src/assets/img/icons/scan.png";
import SettingsIcon from "../src/assets/img/icons/item.png";
import LogoutIcon from "../src/assets/img/icons/logout.png";
import CloseIcon from "../src/assets/img/icons/close.png";
import ArrowRightIcon from "../src/assets/img/icons/arrow.png"; // Import your custom icon
import { useNavigate } from "react-router-dom";
import LocalMallRoundedIcon from "@mui/icons-material/LocalMallRounded";

const Sidebar = ({ open, onClose }) => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const selectedMfr = JSON.parse(localStorage.getItem("selectedMfr"));

  const handleLogout = () => {
    localStorage.clear();
    const prefix = localStorage.getItem("urlPrefix");
    if (prefix) {
      navigate(`/${prefix}`);
    } else {
      navigate("/login");
    }
    onClose(); // Close the sidebar after logout
  };

  const handleNavigation = (path) => {
    navigate(path);
    onClose(); // Close the sidebar after navigation
  };

  const createNewItems = [
    {
      text: "Create User",
      icon: UserIcon,
      path: "/createuser",
      roles: ["MFR", "SUPERADMIN", "COMFR", "SEMIMFR"],
    },
    {
      text: "View User",
      icon: UserIcon,
      path: "/userlist",
      roles: ["MFR", "SUPERADMIN", "COMFR"],
    },
    {
      text: "View Karigar Stat",
      icon: UserIcon,
      path: "/status/karigarstat",
      roles: ["MFR", "SUPERADMIN", "COMFR"],
    },
    {
      text: "Metal",
      icon: MetalIcon,
      path: "/createmetal",
      roles: ["MFR", "SUPERADMIN", "COMFR"],
    },
    {
      text: "Item",
      icon: ItemIcon,
      path: "/createitem",
      roles: ["MFR", "SUPERADMIN", "COMFR"],
    },
    {
      text: "Design",
      icon: DesignIcon,
      path: "/createdesign",
      roles: ["MFR", "SUPERADMIN", "COMFR"],
    },
    {
      text: "Design Catalogue",
      icon: DesignIcon,
      path: "/createdesigncatalogue",
      roles: ["MFR", "SUPERADMIN", "COMFR"],
    },
    {
      text: "Stone",
      icon: FinishIcon,
      path: "/createstone",
      roles: ["MFR", "SUPERADMIN", "COMFR"],
    },
  ];

  const shouldShowCreateNew =
    user?.role &&
    createNewItems.some((item) =>
      item.roles.includes(user?.role.toUpperCase())
    );

  const otherItems = [
    { text: "Scan QR", icon: AnalyticsIcon, path: "/qrscan" },
    { text: "Scan Stock QR", icon: AnalyticsIcon, path: "/stockscan" }, // Assuming a route for Analytics
    // { text: 'App Settings', icon: SettingsIcon, path: '/settings' }, // Assuming a route for App Settings
  ];

  const upgradedFilter = [
    {
      text: "Upgraded Filter",
      icon: LocalMallRoundedIcon,
      path: "/admin-dash",
      roles: [""],
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: "#0A2E31", // Deep navy background
          backgroundImage: "linear-gradient(180deg, #0D3B3E 0%, #0A2E31 100%)",
          color: "white",
          width: 288,
          borderRight: "none",
        },
        "& .MuiList-root": { py: 0 },
        "& .MuiListItemButton-root": {
          borderRadius: "8px",
          py: 1,
          px: 1.25,
          mb: 0.25,
          transition: "background-color 150ms ease",
          "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" },
        },
        "& .MuiListItemIcon-root": { color: "inherit" },
        "& .MuiListItemText-primary": { fontSize: "0.875rem", fontWeight: 500 },
        // Trailing chevrons: quieter so the labels lead
        "& img[alt='Arrow Right']": {
          width: "18px !important",
          height: "18px !important",
          opacity: 0.45,
        },
        // Section headings ("Create New", "Other Items", …)
        "& .sidebar-section-title": {
          fontSize: "0.6875rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          opacity: 0.6,
          mb: 1,
          px: 1.25,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "relative",
            padding: "16px",
            paddingTop: "32px", // Add top space for the close icon
          }}
        >
          {/* Close Icon - positioned top-right above */}
          <IconButton
            onClick={onClose}
            color="inherit"
            sx={{
              position: "absolute",
              top: "8px",
              right: "8px",
              padding: 0,
            }}
          >
            <img
              src={CloseIcon}
              alt="Close"
              style={{ width: 20, height: 20 }}
            />
          </IconButton>

          {/* Row with store name on left and KRAMASYA on right */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {selectedMfr?.storeDetails?.name || ""}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 500, fontSize: "0.85rem", opacity: 0.8 }}
            >
              KRAMASYA
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)" }} />

        {/* User Info */}
        <Box sx={{ padding: "16px" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            {user?.name}
          </Typography>
          <Typography variant="body2" color="white">
            {user?.role}
          </Typography>
          <Typography variant="body2" color="white">
            {user?.mobile}
          </Typography>

          {/* You might want an edit icon here */}
        </Box>
        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)" }} />

        {/* Create New Section */}
        {shouldShowCreateNew && (
          <Box sx={{ padding: "16px" }}>
            <Typography variant="subtitle1" className="sidebar-section-title">
              Create New
            </Typography>
            <List>
              {createNewItems
                .filter((item) =>
                  item.roles.includes(user?.role?.toUpperCase())
                )
                .map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton onClick={() => handleNavigation(item.path)}>
                      <ListItemIcon sx={{ minWidth: 36, mr: 1 }}>
                        <img
                          src={item.icon}
                          alt={item.text}
                          style={{ width: 24, height: 24 }}
                        />
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                      <ListItemIcon sx={{ minWidth: "auto", ml: 2 }}>
                        {" "}
                        {/* Add arrow to the right */}
                        <img
                          src={ArrowRightIcon}
                          alt="Arrow Right"
                          style={{ width: 24, height: 24 }}
                        />
                      </ListItemIcon>
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          </Box>
        )}
        {shouldShowCreateNew && (
          <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)" }} />
        )}

        {/* Other Items Section */}
        <Box sx={{ padding: "16px" }}>
          <Typography variant="subtitle1" className="sidebar-section-title">
            Other Items
          </Typography>
          <List>
            {/* QR scanning isn't available to customers */}
            {(user?.role === "Customer" ? [] : otherItems).map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton onClick={() => handleNavigation(item.path)}>
                  <ListItemIcon sx={{ minWidth: 36, mr: 1 }}>
                    <img
                      src={item.icon}
                      alt={item.text}
                      style={{ width: 24, height: 24 }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                  <ListItemIcon sx={{ minWidth: "auto", ml: 2 }}>
                    <img
                      src={ArrowRightIcon}
                      alt="Arrow Right"
                      style={{ width: 24, height: 24 }}
                    />
                  </ListItemIcon>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {upgradedFilter[0].roles.includes(user?.role.toUpperCase()) && (
          <Box sx={{ padding: "16px" }}>
            <Typography variant="subtitle1" className="sidebar-section-title">
              Filters Upgraded
            </Typography>

            <List>
              {upgradedFilter.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton onClick={() => handleNavigation(item.path)}>
                    <ListItemIcon sx={{ minWidth: 36, mr: 1 }}>
                      <IconButton size="small">
                        <item.icon sx={{ fontSize: 24, fill: "#0D7377" }} />
                      </IconButton>
                    </ListItemIcon>

                    <ListItemText primary={item.text} />

                    <ListItemIcon sx={{ minWidth: "auto", ml: 2 }}>
                      <img
                        src={ArrowRightIcon}
                        alt="Arrow Right"
                        style={{ width: 24, height: 24 }}
                      />
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Upgraded Filter Section */}

        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)" }} />

        {/* Logout */}
        <Box sx={{ mt: "auto", padding: "16px" }}>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon sx={{ minWidth: 36, mr: 1 }}>
                <img
                  src={LogoutIcon}
                  alt="Logout"
                  style={{ width: 24, height: 24 }}
                />
              </ListItemIcon>
              <ListItemText primary="Logout" />
              {/* <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
                <img src={ArrowRightIcon} alt="Arrow Right" style={{ width: 24, height: 24 }} />
              </ListItemIcon> */}
            </ListItemButton>
          </ListItem>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
